import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  SeasonEligibility,
  So5LeaderboardSeasonality,
} from '@sorare/core/src/__generated__/globalTypes';
import { ShieldButton } from '@sorare/core/src/atoms/buttons/ShieldButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { ArenaCashBg } from '@sorare/core/src/atoms/icons/ArenaCashBg';
import { ArenaCashWordmark } from '@sorare/core/src/atoms/icons/ArenaCashWordmark';
import { ArenaEssenceBg } from '@sorare/core/src/atoms/icons/ArenaEssenceBg';
import { ArenaEssenceWordmark } from '@sorare/core/src/atoms/icons/ArenaEssenceWordmark';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons/index.tsx';
import { iconClock } from '@sorare/core/src/atoms/icons/system/iconClock.ts';
import { Centered } from '@sorare/core/src/atoms/layout/Centered';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL, BrandXXS, LabelS } from '@sorare/core/src/atoms/typography';
import { ShieldShape } from '@sorare/core/src/atoms/ui/ShieldShape';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { SportSeasonalityIcon } from '@sorare/core/src/components/SportSeasonalityIcon';
import { useConfigContext } from '@sorare/core/src/contexts/config/index.ts';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft/index.tsx';

import { ModeIcon } from '#src/components/ModeIcon/index.tsx';
import { PickerHeader } from '#src/components/pickers/PickerHeader/index.tsx';
import { useCurrentBoardMode } from '#src/hooks/useCurrentBoardMode.tsx';
import { Mode, modeColors, modeMessages } from '#src/lib/gameMode.ts';

import proAsset from './assets/pro-asset.webp';

export { Mode };

const allModes = [Mode.PRO, Mode.ARENA_CASH, Mode.ARENA_ESSENCE] as const;

const CardsList = styled(Vertical)`
  padding: var(--unit) var(--double-unit) var(--double-unit);
`;

const CardBezel = styled(Bezel)`
  display: flex;
  cursor: pointer;

  &.full {
    width: 100%;
    align-items: flex-end;
    text-align: left;
    overflow: hidden;
  }

  &.arena {
    flex: 1;
    min-width: 0;
    align-items: center;
    justify-content: center;
  }
`;

const ProBgImage = styled.img`
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: auto;
  pointer-events: none;
`;

const ModeCardContent = styled(Vertical).attrs({ gap: 1 })`
  padding: var(--double-unit);
  width: 100%;
  position: relative;
  min-height: 120px;
`;

const ArenaBgWrapper = styled(Centered)`
  position: absolute;
  inset: 0;
  pointer-events: none;
  color: var(--c-white);
  opacity: 0.1;
`;

const CompetitiveDivider = styled(Horizontal).attrs({ gap: 0, center: true })`
  padding: var(--unit) 0;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
`;

const DividerCenter = styled(Vertical).attrs({ gap: 1, center: true })`
  padding: 0 var(--unit);
`;

const ArenaRow = styled(Horizontal).attrs({ gap: 1 })`
  width: 100%;
`;

const DropdownContent = styled.div`
  background: var(--c-black);
`;

type SinglePlayerCardProps = {
  mode: Mode;
  selected: boolean;
  onClick: () => void;
};

const SinglePlayerCard = ({ mode, onClick }: SinglePlayerCardProps) => {
  const color = modeColors[mode];
  return (
    <CardBezel
      className="full"
      background="conic-gradient(from 90deg at 3px 3px, var(--c-nd-50) 25%, transparent 0) 0 0/5px 5px"
      borderColor="var(--c-nd-150)"
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      <ModeCardContent>
        <ProBgImage src={proAsset} alt="" />
        <Horizontal>
          <ShieldShape
            size="sm"
            orientation="up"
            borderColor={color}
            icon={<ModeIcon mode={mode} />}
          />
          <BrandL color="var(--c-white)">
            <FormattedMessage {...modeMessages[mode]} />
          </BrandL>
        </Horizontal>
        <LabelS color="var(--c-nd-400)">
          <FormattedMessage defaultMessage="Climb all season long." />
        </LabelS>
      </ModeCardContent>
    </CardBezel>
  );
};

type ArenaCardProps = {
  mode: Mode;
  label: ReactNode;
  subtitle: ReactNode;
  onClick: () => void;
};

const ArenaCardComponent = ({
  mode,
  label,
  subtitle,
  onClick,
}: ArenaCardProps) => {
  const color = modeColors[mode];
  const bgIcon =
    mode === Mode.ARENA_CASH ? <ArenaCashBg /> : <ArenaEssenceBg />;
  return (
    <CardBezel
      className="arena"
      background="conic-gradient(from 90deg at 3px 3px, var(--c-nd-50) 25%, transparent 0) 0 0/5px 5px"
      borderColor={`linear-gradient(to bottom, var(--c-nd-150) 70%, ${color} 100%)`}
      boxShadow={`inset 0px -20px 20px -20px ${color}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      <ArenaBgWrapper>{bgIcon}</ArenaBgWrapper>
      <ModeCardContent
        style={{
          alignItems: 'center',
          textAlign: 'center',
          padding: 'var(--quadruple-unit) var(--triple-unit)',
        }}
      >
        {label}
        <BrandXXS color="var(--c-white)" className="text-center">
          {subtitle}
        </BrandXXS>
      </ModeCardContent>
    </CardBezel>
  );
};

type Props = {
  modes?: readonly Mode[];
  onSelect: (mode: Mode) => void;
};

export const ModePicker = ({ modes = allModes, onSelect }: Props) => {
  const selected = useCurrentBoardMode();
  const { formatMessage } = useIntl();
  const { so5 } = useConfigContext();

  if (modes.length <= 1) return null;

  return (
    <Dropdown
      align="center"
      gap={8}
      label={props => (
        <ShieldButton
          type="button"
          aria-label={formatMessage(modeMessages[selected])}
          size="md"
          borderColor={modeColors[selected]}
          icon={<ModeIcon mode={selected} />}
          orientation="up"
          {...props}
        />
      )}
    >
      {({ closeDropdown }) => (
        <DropdownContent>
          <PickerHeader onClose={closeDropdown}>
            <FormattedMessage defaultMessage="CHOOSE MODE" />
          </PickerHeader>
          <CardsList>
            <SinglePlayerCard
              mode={Mode.PRO}
              selected={selected === Mode.PRO}
              onClick={() => {
                onSelect(Mode.PRO);
                closeDropdown();
              }}
            />

            <CompetitiveDivider>
              <DividerLine />
              <DividerCenter>
                <ShieldShape
                  size="xxs"
                  orientation="up"
                  borderColor={modeColors[Mode.ARENA_CASH]}
                  icon={<ModeIcon mode={Mode.ARENA_CASH} size={2} />}
                />
                <BrandXXS color="var(--c-white)">
                  <FormattedMessage defaultMessage="COMPETITIVE MODES" />
                </BrandXXS>
                {!!so5.nextSo5Fixture?.cutOffDate && (
                  <Horizontal gap={0.5} center>
                    <FontAwesomeIcon
                      icon={iconClock}
                      color="var(--c-yellow-400)"
                      size="xs"
                    />
                    <BrandXXS color="var(--c-yellow-400)">
                      <TimeLeft time={so5.nextSo5Fixture.cutOffDate} />
                    </BrandXXS>
                  </Horizontal>
                )}
              </DividerCenter>
              <DividerLine />
            </CompetitiveDivider>

            <ArenaRow>
              <ArenaCardComponent
                mode={Mode.ARENA_CASH}
                label={<ArenaCashWordmark />}
                subtitle={
                  <SportSeasonalityIcon
                    color="var(--c-white)"
                    seasonality={So5LeaderboardSeasonality.IN_SEASON}
                    size={2}
                  />
                }
                onClick={() => {
                  onSelect(Mode.ARENA_CASH);
                  closeDropdown();
                }}
              />
              <ArenaCardComponent
                mode={Mode.ARENA_ESSENCE}
                label={<ArenaEssenceWordmark />}
                subtitle={
                  <SportSeasonalityIcon
                    seasonality={SeasonEligibility.CLASSIC}
                    color="var(--c-nd-600-opaque)"
                    size={2}
                  />
                }
                onClick={() => {
                  onSelect(Mode.ARENA_ESSENCE);
                  closeDropdown();
                }}
              />
            </ArenaRow>
          </CardsList>
        </DropdownContent>
      )}
    </Dropdown>
  );
};
